import { z } from "zod";

// Canonical shape of the iframe vars that flow through sessionStorage and
// window (__BOOKING_IFRAME_VARS__). Strict: rejects the whole payload if any
// optional field has the wrong type, so we never call setters with garbage.
export const bookingIframeVarsSchema = z.object({
  coachId: z.number(),
  coachName: z.string(),
  customerId: z.number(),
  memberName: z.string().optional(),
  memberEmail: z.string().optional(),
  memberPhone: z.string().optional(),
  rescheduleSessionId: z.number().int().positive().optional(),
  isTrial: z.boolean().optional(),
  trialEndDate: z.string().optional(),
  // Coach/Admin who initiated this booking from Glepnir's internal scheduling tool, forwarded
  // back to Glepnir as actor_user_id so the Session Timeline attributes the booking correctly
  // instead of falling back to the member (COA-567). Absent for member-initiated bookings.
  bookedByUserId: z.number().int().positive().optional(),
  // Gates the "Message Your Coach" step, Caladan resolves it from PostHog and injects it here.
  preSessionNotesEnabled: z.boolean().optional(),
  // Gates the "Book More Sessions" CTA, Caladan resolves it from PostHog and injects it here.
  multiSessionBookingEnabled: z.boolean().optional(),
});

// postMessage payloads are accepted with two relaxations over the canonical
// schema: coachName must be non-empty (we don't want a blank UI from a stray
// "" sent by a host), and memberPhone can arrive as a number and gets coerced
// to a string for downstream consumers.
export const bookingIframeVarsMessagePayloadSchema =
  bookingIframeVarsSchema.extend({
    coachName: z.string().min(1),
    memberPhone: z
      .union([z.string(), z.number()])
      .optional()
      .transform((value) =>
        typeof value === "number" ? String(value) : value,
      ),
  });

export type BookingIframeVars = z.infer<typeof bookingIframeVarsSchema>;
